class SodBannerTimer extends HTMLElement {
  #controller;
  #observer;
  #frame = 0;
  #held = false;
  #startedAt = 0;
  #index = 0;
  #onScreen = false;

  connectedCallback() {
    this.clips = [...this.querySelectorAll('[data-timer-clip]')];
    this.tiles = [...this.querySelectorAll('[data-timer-tile]')];
    if (this.clips.length === 0) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.tiles.forEach((tile, index) => tile.addEventListener('click', () => this.select(index), { signal }));

    this.stillDuration = Number(this.dataset.stillSeconds) || 5;

    for (const video of this.#videos()) {
      video.loop = false;
      video.autoplay = false;

      video.preload = 'none';
      video.pause();
      video.addEventListener('ended', () => this.#advance(), { signal });
    }

    document.addEventListener('visibilitychange', () => this.#sync(), { signal });

    if (window.Shopify?.designMode) {
      document.addEventListener(
        'shopify:block:select',
        (event) => {
          const index = this.clips.indexOf( (event.target));
          if (index === -1) return;
          this.#held = true;
          this.select(index);
        },
        { signal }
      );
      document.addEventListener(
        'shopify:block:deselect',
        (event) => {
          if (!this.clips.includes( (event.target))) return;
          this.#held = false;
          this.#startedAt = performance.now();
          this.#sync();
        },
        { signal }
      );
    }

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.#onScreen = entry.isIntersecting;
        this.#sync();
      },
      { threshold: 0.2 }
    );
    this.#observer.observe(this);

    this.select(0);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#onScreen = false;
    this.#pause();
  }

  select(index) {
    this.#index = ((index % this.clips.length) + this.clips.length) % this.clips.length;

    this.clips.forEach((clip, i) => clip.toggleAttribute('data-current', i === this.#index));
    this.tiles.forEach((tile, i) => {
      tile.toggleAttribute('data-current', i === this.#index);
      if (tile instanceof HTMLButtonElement) tile.setAttribute('aria-pressed', i === this.#index ? 'true' : 'false');
      if (i !== this.#index) tile.style.removeProperty('--sod-banner-progress');
    });

    for (const video of this.#videos()) video.pause();

    const video = this.#current();
    if (video) {
      try {
        video.currentTime = 0;
      } catch {
      }
    }

    this.#startedAt = performance.now();
    this.#report(0);
    this.#sync();
  }

  get #shouldRun() {
    return this.#onScreen && !document.hidden && !matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #sync() {
    if (this.#shouldRun) this.#play();
    else this.#pause();
  }

  #play() {
    const video = this.#current();
    if (video) {
      video.preload = 'auto';
      video.play().catch(() => {});
    }
    if (!this.#frame) this.#frame = requestAnimationFrame(() => this.#tick());
  }

  #pause() {
    this.#current()?.pause();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #tick() {
    this.#frame = 0;
    if (!this.#shouldRun) return;

    const video = this.#current();

    const running = video != null && video.duration > 0 && !video.error && !video.paused;
    const progress = running
      ? video.currentTime / video.duration
      : (performance.now() - this.#startedAt) / (this.stillDuration * 1000);

    this.#report(progress);

    if (progress >= 1 && !running) {
      if (this.#advance()) return;
      this.#startedAt = performance.now();
    }

    this.#frame = requestAnimationFrame(() => this.#tick());
  }

  #advance() {
    if (this.#held) return false;
    this.select(this.clips.length > 1 ? this.#index + 1 : 0);
    return true;
  }

  #report(progress) {
    const tile = this.tiles[this.#index];
    tile?.style.setProperty('--sod-banner-progress', String(Math.min(Math.max(progress, 0), 1)));
  }

  #current() {
    return this.clips[this.#index]?.querySelector('video') ?? null;
  }

  #videos() {
    return this.querySelectorAll('video');
  }
}

if (!customElements.get('sod-banner-timer')) customElements.define('sod-banner-timer', SodBannerTimer);
